import React from 'react';
import {
  Inbox, PackageCheck, ShieldCheck, AlertTriangle, CheckCircle2, Send, Check,
} from 'lucide-react';

/**
 * Ordered list of MIR workflow stages + their metadata.
 * Kept in ONE place so backend + frontend stay in sync.
 */
export const MIR_STAGES = [
  { key: 'awaiting_docs',         label: 'Awaiting docs',   short: 'Awaiting',  icon: Inbox,         color: '#f59e0b', softBg: '#fef3c7', softFg: '#92400e', desc: 'Client hasn\u2019t supplied the docs yet.' },
  { key: 'docs_with_csm',          label: 'Docs with CSM',   short: 'With CSM',  icon: PackageCheck,  color: '#22c55e', softBg: '#dcfce7', softFg: '#166534', desc: 'CSM has the docs; checking before sending to Credit.' },
  { key: 'pending_credit_review',  label: 'Credit review',   short: 'Credit',    icon: ShieldCheck,   color: '#6366f1', softBg: '#e0e7ff', softFg: '#3730a3', desc: 'Credit team is reviewing the submitted docs.' },
  { key: 'changes_requested',      label: 'Changes needed',  short: 'Changes',   icon: AlertTriangle, color: '#e11d48', softBg: '#fee2e2', softFg: '#991b1b', desc: 'Credit sent it back to CSM with notes.' },
  { key: 'credit_approved',        label: 'Approved',        short: 'Approved',  icon: CheckCircle2,  color: '#059669', softBg: '#d1fae5', softFg: '#065f46', desc: 'Credit signed off. Broker/CSM to return to lender.' },
  { key: 'sent_to_lender',         label: 'Returned',        short: 'Returned',  icon: Send,          color: '#065f46', softBg: '#064e3b', softFg: '#a7f3d0', desc: 'Docs returned to lender; MIR closed.' },
];

// Stages that are shown as steps in the horizontal stepper (Changes-requested
// is a diverging branch, not a step in the happy path).
const STEPPER_ORDER = [
  'awaiting_docs',
  'docs_with_csm',
  'pending_credit_review',
  'credit_approved',
  'sent_to_lender',
];

export function stageMeta(stageKey) {
  return MIR_STAGES.find((s) => s.key === stageKey) || MIR_STAGES[0];
}

const WAITING_ON_META = {
  client:      { label: 'Client',      color: '#f59e0b', softBg: '#fef3c7', softFg: '#92400e' },
  csm:         { label: 'CSM',         color: '#22c55e', softBg: '#dcfce7', softFg: '#166534' },
  credit:      { label: 'Credit Team', color: '#6366f1', softBg: '#e0e7ff', softFg: '#3730a3' },
  broker:      { label: 'Broker',      color: '#0ea5e9', softBg: '#e0f2fe', softFg: '#075985' },
  lender:      { label: 'Lender',      color: '#065f46', softBg: '#d1fae5', softFg: '#065f46' },
  third_party: { label: 'Third Party', color: '#8b5cf6', softBg: '#f3e8ff', softFg: '#6b21a8' },
};
/**
 * Resolve a waiting_on value into display meta.
 * Supports both generic buckets ('client', 'csm', 'credit', ...) and a
 * specific-user form 'user:<id>'. For the latter, callers should pass the
 * `team` array so we can show the resolved name; otherwise a generic
 * "Team member" fallback is used.
 */
export function waitingOnMeta(key, team) {
  if (!key) return null;
  if (typeof key === 'string' && key.startsWith('user:')) {
    const uid = key.slice(5);
    const u = (team || []).find((x) => x.id === uid);
    return {
      label: u ? u.name : 'Team member',
      color: '#a855f7', softBg: '#f3e8ff', softFg: '#6b21a8',
      isUser: true, userId: uid, userName: u?.name || null, userRole: u?.role || null,
    };
  }
  return WAITING_ON_META[key] || null;
}

/**
 * MirWorkflowStepper — a compact horizontal step indicator used at the top
 * of the MIR detail dialog. Click a step to transition to it.
 */
export default function MirWorkflowStepper({ currentStage, onGo, onRequestChanges }) {
  const currentIdx = STEPPER_ORDER.indexOf(currentStage);
  const isChanges = currentStage === 'changes_requested';
  return (
    <div className="w-full" data-testid="mir-workflow-stepper">
      <div className="flex items-stretch gap-1">
        {STEPPER_ORDER.map((key, idx) => {
          const meta = stageMeta(key);
          const Icon = meta.icon;
          const done = currentIdx > idx;
          const active = currentIdx === idx || (isChanges && key === 'pending_credit_review');
          return (
            <button
              key={key}
              type="button"
              onClick={() => onGo && onGo(key)}
              className="group flex-1 relative rounded-lg border transition-all hover:scale-[1.02]"
              style={{
                background: active ? meta.softBg : done ? '#f8fafc' : '#ffffff',
                borderColor: active ? meta.color : done ? '#cbd5e1' : '#e2e8f0',
              }}
              data-testid={`mir-stepper-${key}`}
              title={meta.desc}
            >
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    background: active ? meta.color : done ? '#94a3b8' : '#e2e8f0',
                    color: '#ffffff',
                  }}
                >
                  {done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-[9.5px] font-black uppercase tracking-widest leading-tight" style={{ color: active ? meta.softFg : done ? '#475569' : '#94a3b8' }}>
                    Step {idx + 1}
                  </div>
                  <div className="text-[11px] font-black leading-tight truncate" style={{ color: active ? meta.softFg : done ? '#0f172a' : '#94a3b8' }}>
                    {meta.short}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {isChanges && (
        <div
          className="mt-1.5 rounded-lg px-2.5 py-1.5 flex items-center gap-2"
          style={{ background: '#fee2e2', border: '1px solid #fca5a5' }}
        >
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#b91c1c' }} />
          <span className="text-[11px] font-black uppercase tracking-wider" style={{ color: '#7f1d1d' }}>
            Changes requested by Credit — currently back with CSM
          </span>
          <button
            type="button"
            onClick={() => onRequestChanges && onRequestChanges('resend')}
            className="ml-auto text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md bg-white border border-rose-300 hover:bg-rose-50 transition-colors"
            style={{ color: '#7f1d1d' }}
            data-testid="mir-stepper-resend"
          >
            Resubmit
          </button>
        </div>
      )}
    </div>
  );
}
